import React from 'react'
import toast from 'react-hot-toast'
import { useApp } from '../context/AppContext'
import { Icon, useT, useIsMobile } from '../shared'
import { useOrgName } from "../shared/BrandLogo"
import { TableWrap, Btn, Badge, Tabs, QrModal } from '../shared/components'
import {
  getAdminTest,
  upsertTest,
  updateTest,
  deleteTest,
  addTestQuestion,
  updateTestQuestion,
  deleteTestQuestion,
  getTestQr,
} from '../api/tests.api'

/* ============================================================
   Test Editor — FR-7.1
   Admin/Trainer authoring of pretest and posttest per program.
   ============================================================ */

const KINDS = ['pretest', 'posttest']

export default function TestEditorScreen() {
  const { lang, currentProgramId, currentProgram } = useApp()
  const orgName = useOrgName()
  const isMobile = useIsMobile()
  const [kind, setKind] = React.useState('pretest')
  const [test, setTest] = React.useState(null)
  const [loading, setLoading] = React.useState(false)
  const [editingMeta, setEditingMeta] = React.useState(false)
  const [editingQ, setEditingQ] = React.useState(null) // question being edited, or {} for new
  const [qrOpen, setQrOpen] = React.useState(false)

  const reload = React.useCallback(() => {
    if (!currentProgramId) return
    setLoading(true)
    getAdminTest(currentProgramId, kind)
      .then(setTest)
      .catch((e) => {
        if (e?.response?.status === 404) {
          setTest(null)
        } else {
          toast.error(e?.response?.data?.error || e.message)
        }
      })
      .finally(() => setLoading(false))
  }, [currentProgramId, kind])

  React.useEffect(() => { reload() }, [reload])

  const handleCreateTest = async () => {
    try {
      const created = await upsertTest(currentProgramId, kind, {
        title: kind === 'pretest'
          ? (lang === 'id' ? 'Pretest' : 'Pretest')
          : (lang === 'id' ? 'Posttest' : 'Posttest'),
        time_limit: kind === 'pretest' ? 20 : 30,
        max_attempts: kind === 'pretest' ? 1 : 2,
      })
      setTest({ ...created, questions: [] })
      toast.success(lang === 'id' ? 'Tes dibuat' : 'Test created')
    } catch (e) {
      toast.error(e?.response?.data?.error || e.message)
    }
  }

  const handleDeleteTest = async () => {
    if (!window.confirm(
      lang === 'id'
        ? 'Hapus tes ini beserta semua soal dan riwayat percobaan? Tidak bisa dibatalkan.'
        : 'Delete this test and all its questions and attempts? This cannot be undone.'
    )) return
    try {
      await deleteTest(currentProgramId, kind)
      setTest(null)
      toast.success(lang === 'id' ? 'Tes dihapus' : 'Test deleted')
    } catch (e) {
      toast.error(e?.response?.data?.error || e.message)
    }
  }

  const handleDeleteQuestion = async (qid) => {
    if (!window.confirm(lang === 'id' ? 'Hapus soal ini?' : 'Delete this question?')) return
    try {
      await deleteTestQuestion(qid)
      reload()
    } catch (e) {
      toast.error(e?.response?.data?.error || e.message)
    }
  }

  return (
    <div className="main-inner">
      <div className="page-header">
        <div className="page-header-text">
          <div className="eyebrow">
            {currentProgram?.code?.toUpperCase() || orgName} · {lang === 'id' ? 'Assessment' : 'Assessment'}
          </div>
          <h1 className="h1" style={{ marginTop: 6 }}>
            {lang === 'id' ? 'Editor Pretest & Posttest' : 'Pretest & Posttest Editor'}
          </h1>
          <div className="muted mt-1">
            {lang === 'id'
              ? `Kelola pretest dan posttest untuk ${currentProgram?.name || 'program ini'}.`
              : `Manage pretest and posttest for ${currentProgram?.name || 'this program'}.`}
          </div>
        </div>
        {/* Hanya muncul kalau tesnya memang ada — memproyeksikan QR menuju tes
            yang belum dibuat cuma mengantar sekelas ke layar kosong. */}
        {test && (
          <Btn
            variant="secondary"
            icon={<Icon name="qr" size={14} />}
            onClick={() => setQrOpen(true)}
          >
            {lang === 'id' ? 'Tampilkan QR' : 'Show QR'}
          </Btn>
        )}
      </div>

      <Tabs
        tabs={[
          { id: 'pretest', label: lang === 'id' ? 'Pretest' : 'Pretest' },
          { id: 'posttest', label: lang === 'id' ? 'Posttest' : 'Posttest' },
        ]}
        value={kind}
        onChange={setKind}
      />

      <div style={{ marginTop: 16 }}>
        {loading ? (
          <div className="muted">{lang === 'id' ? 'Memuat...' : 'Loading...'}</div>
        ) : !test ? (
          <div className="card" style={{ textAlign: 'center', padding: 48 }}>
            <div style={{
              width: 56, height: 56, borderRadius: '50%',
              background: 'var(--surface-2)', display: 'grid', placeItems: 'center',
              margin: '0 auto 16px',
            }}>
              <Icon name="file" size={24} color="var(--text-tertiary)" />
            </div>
            <p className="muted">
              {lang === 'id'
                ? `Belum ada ${kind === 'pretest' ? 'pretest' : 'posttest'} untuk program ini.`
                : `No ${kind} configured for this program yet.`}
            </p>
            <Btn variant="primary" icon={<Icon name="plus" size={14} />}
              onClick={handleCreateTest} style={{ marginTop: 16 }}>
              {lang === 'id' ? 'Buat sekarang' : 'Create now'}
            </Btn>
          </div>
        ) : (
          <>
            <div className="card">
              <div className="card-header">
                <div className="h3">{lang === 'id' ? 'Pengaturan tes' : 'Test settings'}</div>
                <div className="row gap-2">
                  <Btn variant="ghost" size="sm" icon={<Icon name="edit" size={12} />}
                    onClick={() => setEditingMeta(true)}>
                    {lang === 'id' ? 'Ubah' : 'Edit'}
                  </Btn>
                  <Btn variant="ghost" size="sm" icon={<Icon name="trash" size={12} />}
                    onClick={handleDeleteTest} style={{ color: 'var(--red-60)' }}>
                  </Btn>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: 16, padding: 16 }}>
                <div>
                  <div className="tiny muted">{lang === 'id' ? 'Judul' : 'Title'}</div>
                  <div className="mt-1" style={{ fontWeight: 500 }}>{test.title}</div>
                </div>
                <div>
                  <div className="tiny muted">{lang === 'id' ? 'Batas waktu' : 'Time limit'}</div>
                  <div className="mt-1 mono">
                    {test.time_limit != null
                      ? `${test.time_limit} ${lang === 'id' ? 'menit' : 'min'}`
                      : (lang === 'id' ? 'Tanpa batas' : 'No limit')}
                  </div>
                </div>
                <div>
                  <div className="tiny muted">{lang === 'id' ? 'Maks percobaan' : 'Max attempts'}</div>
                  <div className="mt-1 mono">{test.max_attempts ?? '—'}</div>
                </div>
                {/* Keadaan gerbang ditampilkan di sini, bukan hanya di dalam
                    dialognya: sakelar yang baru terlihat setelah dialog dibuka
                    adalah sakelar yang orang lupa sedang menyala. */}
                {kind === 'posttest' && (
                  <div>
                    <div className="tiny muted">
                      {lang === 'id' ? 'Menunggu pelajaran selesai' : 'Waits for lessons'}
                    </div>
                    <div className="mt-1">
                      {test.requires_lessons === false
                        ? <span className="tag amber tiny">{lang === 'id' ? 'Tidak — posttest langsung terbuka' : 'No — posttest is open'}</span>
                        : <span className="tag outline tiny">{lang === 'id' ? 'Ya' : 'Yes'}</span>}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="card mt-4">
              <div className="card-header">
                <div className="h3">
                  {lang === 'id' ? 'Soal' : 'Questions'}{' '}
                  <span className="muted" style={{ fontWeight: 400 }}>
                    ({test.questions?.length || 0})
                  </span>
                </div>
                <Btn variant="primary" size="sm" icon={<Icon name="plus" size={12} />}
                  onClick={() => setEditingQ({})}>
                  {lang === 'id' ? 'Tambah Soal' : 'Add Question'}
                </Btn>
              </div>
              {(test.questions || []).length === 0 ? (
                <div style={{ padding: 24, textAlign: 'center' }} className="muted small">
                  {lang === 'id' ? 'Belum ada soal.' : 'No questions yet.'}
                </div>
              ) : (
                <TableWrap>
                <table className="tbl">
                  <thead>
                    <tr>
                      <th style={{ width: 40 }}>#</th>
                      <th>{lang === 'id' ? 'Soal' : 'Question'}</th>
                      <th style={{ width: 80 }}>{lang === 'id' ? 'Jenis' : 'Type'}</th>
                      <th style={{ width: 60 }}>{lang === 'id' ? 'Poin' : 'Points'}</th>
                      <th style={{ width: 100 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {test.questions.map((q, i) => (
                      <tr key={q.id}>
                        <td className="mono small">{i + 1}</td>
                        <td>
                          <div style={{ maxWidth: 480 }} className="text-truncate">{q.body}</div>
                          {q.type === 'MCQ' && q.correct_answer && (
                            <div className="tiny muted mt-1">
                              {lang === 'id' ? 'Jawaban' : 'Answer'}: <span className="mono">{q.correct_answer}</span>
                            </div>
                          )}
                        </td>
                        <td>
                          <Badge tone={q.type === 'MCQ' ? 'brand' : 'warning'}>
                            {q.type}
                          </Badge>
                        </td>
                        <td className="mono small">{q.points}</td>
                        <td>
                          <div className="row gap-1">
                            <Btn variant="ghost" size="sm" icon={<Icon name="edit" size={12} />}
                              onClick={() => setEditingQ(q)} />
                            <Btn variant="ghost" size="sm" icon={<Icon name="trash" size={12} />}
                              onClick={() => handleDeleteQuestion(q.id)}
                              style={{ color: 'var(--red-60)' }} />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </TableWrap>
              )}
            </div>
          </>
        )}
      </div>

      {editingMeta && test && (
        <TestMetaModal
          test={test}
          onClose={() => setEditingMeta(false)}
          onSaved={(updated) => {
            setTest({ ...test, ...updated })
            setEditingMeta(false)
          }}
          programId={currentProgramId}
          kind={kind}
        />
      )}

      {editingQ && test && (
        <QuestionModal
          programId={currentProgramId}
          kind={kind}
          question={editingQ.id ? editingQ : null}
          onClose={() => setEditingQ(null)}
          onSaved={() => { setEditingQ(null); reload() }}
        />
      )}

      {qrOpen && (
        <TestQrModal
          programId={currentProgramId}
          kind={kind}
          onClose={() => setQrOpen(false)}
        />
      )}
    </div>
  )
}

// ─── Modals ─────────────────────────────────────────────────────────────────

// Pengambil datanya di sini, tampilannya di QrModal bersama — layar Feedback
// memakai modal yang sama untuk QR-nya.
function TestQrModal({ programId, kind, onClose }) {
  const { lang } = useApp()
  const [data, setData] = React.useState(null)
  const [gagal, setGagal] = React.useState(null)

  React.useEffect(() => {
    let batal = false
    getTestQr(programId, kind)
      .then((d) => { if (!batal) setData(d) })
      .catch((e) => { if (!batal) setGagal(e?.response?.data?.error || e.message) })
    return () => { batal = true }
  }, [programId, kind])

  const judul = kind === 'pretest'
    ? (lang === 'id' ? 'QR Pretest' : 'Pretest QR')
    : (lang === 'id' ? 'QR Posttest' : 'Posttest QR')

  return (
    <QrModal
      title={judul}
      url={data?.url}
      qrPngDataUrl={data?.qrPngDataUrl}
      downloadName={`qr-${kind}.png`}
      error={gagal}
      onClose={onClose}
      lang={lang}
    />
  )
}

function TestMetaModal({ test, programId, kind, onClose, onSaved }) {
  const { lang } = useApp()
  const [title, setTitle] = React.useState(test.title || '')
  const [timeLimit, setTimeLimit] = React.useState(test.time_limit ?? '')
  const [maxAttempts, setMaxAttempts] = React.useState(test.max_attempts ?? 1)
  // Ditulis sebagai "langsung terbuka", bukan "butuh pelajaran": yang dicentang
  // orang adalah tindakan yang ia inginkan, bukan syarat yang ia cabut.
  const [langsungTerbuka, setLangsungTerbuka] = React.useState(test.requires_lessons === false)
  const [busy, setBusy] = React.useState(false)

  const submit = async (e) => {
    e.preventDefault()
    setBusy(true)
    try {
      const updated = await updateTest(programId, kind, {
        title,
        time_limit: timeLimit === '' ? null : Number(timeLimit),
        max_attempts: Number(maxAttempts),
        ...(kind === 'posttest' && { requires_lessons: !langsungTerbuka }),
      })
      onSaved(updated)
    } catch (ex) {
      toast.error(ex?.response?.data?.error || ex.message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <ModalShell title={lang === 'id' ? 'Ubah pengaturan tes' : 'Edit test settings'} onClose={onClose}>
      <form onSubmit={submit} className="col gap-3" style={{ padding: 16 }}>
        <label className="col gap-1">
          <span className="small">{lang === 'id' ? 'Judul' : 'Title'}</span>
          <input value={title} onChange={(e) => setTitle(e.target.value)} required
            style={inputStyle} />
        </label>
        <label className="col gap-1">
          <span className="small">{lang === 'id' ? 'Batas waktu (menit, kosong = tanpa batas)' : 'Time limit (minutes, blank = unlimited)'}</span>
          <input type="number" min="0" value={timeLimit}
            onChange={(e) => setTimeLimit(e.target.value)}
            style={inputStyle} />
        </label>
        <label className="col gap-1">
          <span className="small">{lang === 'id' ? 'Maks percobaan' : 'Max attempts'}</span>
          <input type="number" min="1" max="20" value={maxAttempts}
            onChange={(e) => setMaxAttempts(e.target.value)} required
            style={inputStyle} />
        </label>
        {kind === 'posttest' && (
          <label className="col gap-1">
            <span className="row gap-2" style={{ alignItems: 'flex-start' }}>
              <input type="checkbox" checked={langsungTerbuka}
                onChange={(e) => setLangsungTerbuka(e.target.checked)}
                style={{ marginTop: 3 }} />
              <span className="small">
                {lang === 'id'
                  ? 'Buka posttest tanpa menunggu pelajaran selesai'
                  : 'Open the posttest without waiting for lessons'}
              </span>
            </span>
            <span className="tiny muted" style={{ marginLeft: 24 }}>
              {lang === 'id'
                ? 'Untuk pelatihan tatap muka, yang materinya dibahas di ruangan. Bawaannya posttest baru terbuka setelah peserta menyelesaikan seluruh pelajaran.'
                : 'For classroom training where the material is delivered in person. By default the posttest opens only after every lesson is complete.'}
            </span>
          </label>
        )}
      </form>
      <div style={{
        padding: '12px 16px', borderTop: '1px solid var(--border-subtle)',
        display: 'flex', justifyContent: 'flex-end', gap: 8,
      }}>
        <Btn variant="ghost" size="sm" onClick={onClose} disabled={busy}>
          {lang === 'id' ? 'Batal' : 'Cancel'}
        </Btn>
        <Btn variant="primary" size="sm" onClick={submit} disabled={busy}>
          {busy ? (lang === 'id' ? 'Menyimpan...' : 'Saving...') : (lang === 'id' ? 'Simpan' : 'Save')}
        </Btn>
      </div>
    </ModalShell>
  )
}

function QuestionModal({ programId, kind, question, onClose, onSaved }) {
  const { lang } = useApp()
  const editing = !!question
  const [body, setBody] = React.useState(question?.body || '')
  const [type, setType] = React.useState(question?.type || 'MCQ')
  const [points, setPoints] = React.useState(question?.points ?? 1)
  const [orderIndex, setOrderIndex] = React.useState(question?.order_index ?? 0)
  const [options, setOptions] = React.useState(
    Array.isArray(question?.options) && question.options.length > 0
      ? question.options
      : [
          { key: 'A', text: '' },
          { key: 'B', text: '' },
          { key: 'C', text: '' },
          { key: 'D', text: '' },
        ]
  )
  const [correctAnswer, setCorrectAnswer] = React.useState(question?.correct_answer || '')
  const [busy, setBusy] = React.useState(false)

  const submit = async (e) => {
    e.preventDefault()
    if (type === 'MCQ' && !correctAnswer) {
      toast.error(lang === 'id' ? 'Pilih jawaban benar untuk MCQ' : 'Select correct answer for MCQ')
      return
    }
    setBusy(true)
    try {
      const payload = {
        body,
        type,
        points: Number(points),
        order_index: Number(orderIndex),
        options: type === 'MCQ' ? options.filter((o) => o.text.trim()) : null,
        correct_answer: type === 'MCQ' ? correctAnswer : null,
      }
      if (editing) {
        await updateTestQuestion(question.id, payload)
      } else {
        await addTestQuestion(programId, kind, payload)
      }
      onSaved()
    } catch (ex) {
      toast.error(ex?.response?.data?.error || ex.message)
    } finally {
      setBusy(false)
    }
  }

  const updateOption = (i, text) => {
    setOptions(options.map((o, idx) => idx === i ? { ...o, text } : o))
  }

  return (
    <ModalShell
      title={editing
        ? (lang === 'id' ? 'Ubah Soal' : 'Edit Question')
        : (lang === 'id' ? 'Tambah Soal' : 'Add Question')}
      onClose={onClose}
      width={600}
    >
      <form onSubmit={submit} className="col gap-3" style={{ padding: 16 }}>
        <label className="col gap-1">
          <span className="small">{lang === 'id' ? 'Pertanyaan' : 'Question body'}</span>
          <textarea value={body} onChange={(e) => setBody(e.target.value)} required
            style={{ ...inputStyle, height: 'auto', minHeight: 80, resize: 'vertical', padding: 8 }} />
        </label>

        <div className="row gap-3">
          <label className="col gap-1" style={{ flex: 1 }}>
            <span className="small">{lang === 'id' ? 'Jenis' : 'Type'}</span>
            <select value={type} onChange={(e) => setType(e.target.value)} style={inputStyle}>
              <option value="MCQ">MCQ (pilihan ganda)</option>
              <option value="ESSAY">{lang === 'id' ? 'Esai' : 'Essay'}</option>
            </select>
          </label>
          <label className="col gap-1" style={{ width: 100 }}>
            <span className="small">{lang === 'id' ? 'Poin' : 'Points'}</span>
            <input type="number" min="1" value={points}
              onChange={(e) => setPoints(e.target.value)} style={inputStyle} />
          </label>
          <label className="col gap-1" style={{ width: 100 }}>
            <span className="small">{lang === 'id' ? 'Urutan' : 'Order'}</span>
            <input type="number" min="0" value={orderIndex}
              onChange={(e) => setOrderIndex(e.target.value)} style={inputStyle} />
          </label>
        </div>

        {type === 'MCQ' && (
          <div className="col gap-2">
            <span className="small">{lang === 'id' ? 'Pilihan' : 'Options'}</span>
            {options.map((opt, i) => (
              <div key={opt.key} className="row gap-2" style={{ alignItems: 'center' }}>
                <input
                  type="radio"
                  name="correct"
                  checked={correctAnswer === opt.key}
                  onChange={() => setCorrectAnswer(opt.key)}
                  title={lang === 'id' ? 'Jawaban benar' : 'Correct answer'}
                />
                <span className="mono small" style={{ width: 20, flexShrink: 0 }}>{opt.key}.</span>
                <input
                  value={opt.text}
                  onChange={(e) => updateOption(i, e.target.value)}
                  placeholder={lang === 'id' ? `Pilihan ${opt.key}` : `Option ${opt.key}`}
                  style={{ ...inputStyle, flex: 1 }}
                />
              </div>
            ))}
            <span className="tiny muted">
              {lang === 'id'
                ? 'Klik radio di kiri untuk menandai jawaban benar.'
                : 'Click the radio on the left to mark the correct answer.'}
            </span>
          </div>
        )}

        {type === 'ESSAY' && (
          <div style={{
            padding: '10px 12px', background: 'var(--amber-10)',
            border: '1px solid var(--amber-50)', borderRadius: 6,
            display: 'flex', gap: 8, alignItems: 'flex-start',
          }}>
            <Icon name="alert" size={14} color="var(--amber-60)" style={{ flexShrink: 0, marginTop: 2 }} />
            <span className="tiny" style={{ flex: 1, color: 'var(--amber-80)' }}>
              {lang === 'id'
                ? 'Soal esai dinilai manual oleh trainer setelah peserta submit.'
                : 'Essay questions are graded manually by trainers after submission.'}
            </span>
          </div>
        )}
      </form>
      <div style={{
        padding: '12px 16px', borderTop: '1px solid var(--border-subtle)',
        display: 'flex', justifyContent: 'flex-end', gap: 8,
      }}>
        <Btn variant="ghost" size="sm" onClick={onClose} disabled={busy}>
          {lang === 'id' ? 'Batal' : 'Cancel'}
        </Btn>
        <Btn variant="primary" size="sm" onClick={submit} disabled={busy}>
          {busy
            ? (lang === 'id' ? 'Menyimpan...' : 'Saving...')
            : (lang === 'id' ? 'Simpan' : 'Save')}
        </Btn>
      </div>
    </ModalShell>
  )
}

const inputStyle = {
  height: 32,
  padding: '0 8px',
  border: '1px solid var(--border-strong)',
  fontSize: 14,
}

function ModalShell({ title, onClose, width = 480, children }) {
  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)',
        display: 'grid', placeItems: 'center', zIndex: 50,
      }}
      onClick={onClose}
    >
      <div
        className="card"
        onClick={(e) => e.stopPropagation()}
        style={{
          width, maxHeight: '90vh', overflow: 'auto',
          background: 'var(--surface)', boxShadow: 'var(--shadow-lg)',
        }}
      >
        <div className="card-header">
          <div className="h3">{title}</div>
          <Btn variant="ghost" size="sm" icon={<Icon name="close" size={14} />} onClick={onClose} />
        </div>
        {children}
      </div>
    </div>
  )
}
